import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { queryClient } from '@/lib/queryClient';

/*
 * Auth store — persists the JWT and user profile to localStorage so refreshes
 * keep the session. The Axios interceptor reads `token` on every request and
 * fires `logout()` on any 401 (after attempting one Cognito refresh first,
 * see api/client.ts).
 *
 * Shape mirrors the server's auth.js formatUser() output plus the practice.
 *
 * `logout` and both `setSession*` actions clear the TanStack Query cache.
 * Without that, a sign-in as a different user on the same browser tab would
 * re-use the previous user's cached queries — personalized data
 * (notifications' per-user `read_at`, assigned-only lists, dashboards) would
 * briefly appear for the new user until each query's staleTime elapsed and
 * refetched.
 *
 * Cognito-mode additions: `refreshToken` and `expiresAt` are persisted
 * alongside the access token so the refresh-loop survives a full reload.
 * In stub mode they stay null/0.
 */

export interface AuthUser {
    id: string;
    first: string;
    last: string;
    email: string;
    roleId: string;
    roleName: string;
    roleType: string;
    permissions: string[];
    mfa: boolean;
    status: string;
    npi: string | null;
    clientId: string | null;
}

export interface AuthPractice {
    id: string;
    name: string;
}

export interface CognitoTokens {
    accessToken:  string;
    refreshToken: string | null;
    expiresAt:    number;
}

interface AuthState {
    token: string | null;
    refreshToken: string | null;
    expiresAt: number;
    user: AuthUser | null;
    practice: AuthPractice | null;
    setSession: (token: string, user: AuthUser, practice: AuthPractice) => void;
    setCognitoSession: (tokens: CognitoTokens, user: AuthUser, practice: AuthPractice) => void;
    setCognitoTokens: (tokens: CognitoTokens) => void;
    updatePractice: (partial: Partial<AuthPractice>) => void;
    logout: () => void;
    hasPermission: (perm: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            token: null,
            refreshToken: null,
            expiresAt: 0,
            user: null,
            practice: null,
            setSession: (token, user, practice) => {
                queryClient.clear();
                set({ token, refreshToken: null, expiresAt: 0, user, practice });
            },
            setCognitoSession: (tokens, user, practice) => {
                queryClient.clear();
                set({
                    token:        tokens.accessToken,
                    refreshToken: tokens.refreshToken,
                    expiresAt:    tokens.expiresAt,
                    user,
                    practice,
                });
            },
            setCognitoTokens: (tokens) =>
                set({
                    token:        tokens.accessToken,
                    refreshToken: tokens.refreshToken,
                    expiresAt:    tokens.expiresAt,
                }),
            updatePractice: (partial) =>
                set((s) =>
                    s.practice ? { practice: { ...s.practice, ...partial } } : s,
                ),
            logout: () => {
                queryClient.clear();
                set({ token: null, refreshToken: null, expiresAt: 0, user: null, practice: null });
            },
            hasPermission: (perm) => get().user?.permissions.includes(perm) ?? false,
        }),
        {
            name: 'gs.auth',
            partialize: (s) => ({
                token:        s.token,
                refreshToken: s.refreshToken,
                expiresAt:    s.expiresAt,
                user:         s.user,
                practice:     s.practice,
            }),
        },
    ),
);
